import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { Checkbox, Divider } from 'components-ref/atoms'
import { Modal } from 'components-ref/molecules/modal/modal'
import { useModalContext } from 'context/modalContext'
import CreatorsList from 'features/CreatorsList/CreatorsList'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { addUsersToSubscriptionList } from 'services/endpoints/subscriptionList'
import { getSubscriptionModels } from 'services/endpoints/subscriptions'
import colors from 'styles/colors'
import {
  type ICreator,
  type ISubscriptionModelsServerResponse,
} from 'types/interfaces/ICreator'

interface NewListModalProps {
  id: string | undefined
  chosenSort?: string
  chosenOrder?: string
  chosenSearchFilter?: string
  chosenCreators?: string[]
  onClose: () => void
  addedCreators?: string[]
  onCreatorsAdded?: (creators: any[]) => void
}

const useSortSubsTop = () => {
  const { t } = useTranslation()

  return useMemo(
    () => [
      { value: 'name', name: t('name') },
      { value: 'recently_added', name: t('recentlyAdded') },
      { value: 'total_subscriptions', name: t('totalSubscriptions') },
    ],
    [t]
  )
}

const useSubsOrder = () => {
  const { t } = useTranslation()

  return useMemo(
    () => [
      { value: 'ascending', name: t('ascending') },
      { value: 'descending', name: t('descending') },
    ],
    [t]
  )
}

const mapSubscriptionToCreator = (
  item: ISubscriptionModelsServerResponse
): ICreator => {
  const data = item.model_data

  return {
    coverUrl: data.cover ?? '',
    avatarUrl: data.avatar ?? '',
    name: data.name,
    handle: data.username,
    isLive: data.active_stream,
    isActive: data.is_active,
    isOnline: data.is_online,
    price: data.price,
    save: data.discount,
    userId: data.user_id,
    initialSubscriptionDate: data.expires ?? undefined,
    numberOfSubs: data.total_months_subscribed ?? 0,
    registeredAt: 0,
  }
}

export const AddCreatorsToList = ({
  id,
  chosenOrder,
  chosenSort,
  chosenSearchFilter,
  chosenCreators,
  onClose,
  addedCreators = [],
  onCreatorsAdded,
}: NewListModalProps) => {
  const { t } = useTranslation()
  const modalData = useModalContext()
  const queryClient = useQueryClient()
  const sortSubsTop = useSortSubsTop()
  const sortSubsOrder = useSubsOrder()

  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true)
  const [isCreatingSubscriptionList, setIsCreatingSubscriptionList] =
    useState(false)

  const [searchTerm, setSearchTerm] = useState(
    chosenSearchFilter?.toLowerCase() ?? ''
  )
  const [selectedSort, setSelectedSort] = useState(chosenSort ?? 'name')
  const [selectedOrder, setSelectedOrder] = useState(
    chosenOrder ?? 'ascending'
  )
  const [selectedItems, setSelectedItems] = useState<string[]>(
    chosenCreators ?? []
  )
  const [creatorsData, setCreatorsData] = useState<ICreator[]>([])

  useEffect(() => {
    const setSubscriptions = async () => {
      setIsLoadingSubscriptions(true)

      try {
        const data: ISubscriptionModelsServerResponse[] =
          await getSubscriptionModels(id)

        setCreatorsData(data.map(mapSubscriptionToCreator))
      } finally {
        setIsLoadingSubscriptions(false)
      }
    }

    void setSubscriptions()
  }, [id])

  const sortedCreators = useMemo(() => {
    const orderingConst = selectedOrder === 'ascending' ? 1 : -1
    const list = [...creatorsData]

    switch (selectedSort) {
      case 'recently_added':
        return list.sort(
          (a, b) =>
            ((new Date(a.initialSubscriptionDate ?? 0).getTime() || 0) -
              (new Date(b.initialSubscriptionDate ?? 0).getTime() || 0)) *
            orderingConst
        )

      case 'total_subscriptions':
        return list.sort(
          (a, b) =>
            ((a.numberOfSubs ?? 0) - (b.numberOfSubs ?? 0)) * orderingConst
        )

      case 'name':
      default:
        return list.sort(
          (a, b) => a.name.localeCompare(b.name, 'en') * orderingConst
        )
    }
  }, [creatorsData, selectedSort, selectedOrder])

  const displayedCreators = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()

    return sortedCreators.filter(creator => {
      const matchesSearch =
        !term ||
        creator.name.toLowerCase().includes(term) ||
        creator.handle.toLowerCase().includes(term)

      const isNotAlreadyAdded = !addedCreators.includes(creator.userId)

      return matchesSearch && isNotAlreadyAdded
    })
  }, [sortedCreators, searchTerm, addedCreators])

  const selectAll =
    displayedCreators.length > 0 &&
    displayedCreators.every(creator => selectedItems.includes(creator.userId))

  const handleSelect = (userId: string) => {
    setSelectedItems(prev =>
      prev.includes(userId)
        ? prev.filter(selectedId => selectedId !== userId)
        : [...prev, userId]
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(prev =>
        prev.filter(
          selectedId =>
            !displayedCreators.some(creator => creator.userId === selectedId)
        )
      )
      return
    }

    setSelectedItems(prev =>
      Array.from(
        new Set([...prev, ...displayedCreators.map(creator => creator.userId)])
      )
    )
  }
  const handleNextClick = async () => {
    if (selectedItems.length <= 0) {
      addToast('error', t('error:addNameAndMembers'))
      return
    }

    setIsCreatingSubscriptionList(true)

    try {
      const response = await addUsersToSubscriptionList(id, selectedItems)

      onCreatorsAdded?.(response?.data?.creators ?? [])

      queryClient.invalidateQueries(['allSubscriptionLists'])
      modalData.clearModal()
      onClose()
    } finally {
      setIsCreatingSubscriptionList(false)
    }
  }
  return (
    <Modal
      title="Add Creators"
      onClose={onClose}
      hasShadow
      paddingBottom={0}
      maxWidth={40}
      fixedFooter
      className="subscription-list-modal"
      footer={
        <Button
          text={t('add')}
          color="black"
          font="mont-14-normal"
          width="13"
          height="3"
          disabled={isCreatingSubscriptionList}
          clickFn={handleNextClick}
        />
      }
    >
      <SearchWrapper>
        <LayoutHeader
          type="search-with-buttons"
          searchValue={searchTerm}
          searchFn={(term: string) => setSearchTerm(term.toLowerCase())}
          clearFn={() => setSearchTerm('')}
          buttons={[
            {
              type: 'sort',
              elements: {
                first_section: sortSubsTop,
                second_section: sortSubsOrder,
              },
              dontClearModal: true,
            },
          ]}
          applyFn={(sort: string, order: string) => {
            setSelectedSort(sort)
            setSelectedOrder(order)
          }}
        />
      </SearchWrapper>

      <Divider />

      <Selection>
        <Checkbox
          id="subscribers-select-all"
          value="selectAll"
          label={t('selectAll')}
          checked={selectAll}
          handleOnChange={handleSelectAll}
          // disabled={isLoadingSubscriptions || displayedCreators.length === 0}
        />

        <p>
          <span>{selectedItems.length}</span> {t('selected')}
        </p>
      </Selection>

      {isLoadingSubscriptions ? (
        <LoaderWrapper>
          <BeatLoader size={8} />
        </LoaderWrapper>
      ) : displayedCreators.length > 0 ? (
        <CreatorsList
          creators={displayedCreators as any}
          renderLocation="newList"
          selectedItems={selectedItems}
          onSelect={handleSelect}
        />
      ) : (
        <EmptyState>{t('noSubscriptionsFound')}</EmptyState>
      )}
    </Modal>
  )
}

const SearchWrapper = styled.div`
  margin-top: 1rem;
`

const Selection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    font-size: 1.2rem;
    font-weight: 400;
    margin: 0;
    color: ${colors.grey};

    span {
      font-weight: 600;
    }
  }
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 20rem;
`

const EmptyState = styled.p`
  font-size: 1.3rem;
  font-weight: 500;
  color: ${colors.grey};
  text-align: center;
  margin: 4rem 0;
`